import {
  EmployerIndustry,
  JobPosition,
  Product,
} from '@axo/shared/data-access/types';
import { ApplicationDataDateField } from '../../Fields/ApplicationDataDateField';
import { ApplicationDataSelectField } from '../../Fields/ApplicationDataSelectField';
import { ApplicationDataTextField } from '../../Fields/ApplicationDataTextField';
import { ApplicantDataApplicantTabPanelSection } from '../ApplicationDataApplicantTabPanelLayout';
import { ApplicationDataCurrencyField } from '../../Fields/ApplicationDataCurrencyField';
import { ApplicationDataFormValues } from '../../ApplicationDataForm/ApplicationDataFormSchema';
import { useFormContext } from 'react-hook-form';
import { MarketCountryCode } from '@axo/shared/types';
import { Broker } from '@axo/shared/data-access/types/broker';
import { useMainBankOptions } from './options/useMainBankOptions';
import { ApplicationConditionalWrapper } from '../../../ConditionalWrappers/ApplicationConditionalWrapper';
import { useEmploymentStatusOptions } from './options/useEmploymentStatusOptions';
import { ApplicationDataNumberField } from '../../Fields/ApplicationDataNumberField';

interface FinanceSectionProps {
  applicationID: string;
  coApplicantCustomerID: string | undefined;
  coApplicantPersonID: string | undefined;
}

export const FinanceSection: React.FC<FinanceSectionProps> = ({
  applicationID,
  coApplicantCustomerID,
  coApplicantPersonID,
}) => {
  const { setValue, getValues } = useFormContext<ApplicationDataFormValues>();

  const employmentStatusOptions = useEmploymentStatusOptions();
  const mainBankOptions = useMainBankOptions();

  const isCoApplicant = coApplicantPersonID || coApplicantCustomerID;

  return (
    <ApplicantDataApplicantTabPanelSection title="Finance">
      {!isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              productType: [Product.UnsecuredLoan],
              marketCountry: [MarketCountryCode.Finland],
              broker: [Broker.Zmarta],
            },
          ]}
        >
          <ApplicationDataSelectField
            applicationID={applicationID}
            name="MainBank"
            label="Main bank"
            options={mainBankOptions}
          />
        </ApplicationConditionalWrapper>
      )}
      {!isCoApplicant && (
        <ApplicationDataSelectField
          applicationID={applicationID}
          name="EmploymentStatus"
          label="Type of employment"
          options={employmentStatusOptions}
        />
      )}
      {isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            { productType: [Product.UnsecuredLoan, Product.SecuredLoan] },
          ]}
        >
          <ApplicationDataSelectField
            applicationID={applicationID}
            name="CoApplicantEmploymentStatus"
            label="Type of employment"
            options={employmentStatusOptions}
          />
        </ApplicationConditionalWrapper>
      )}
      {!isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[{ marketCountry: [MarketCountryCode.Denmark] }]}
        >
          <ApplicationDataTextField
            applicationID={applicationID}
            name="CompanyOrganizationNumber"
            label="Company CVR"
          />
        </ApplicationConditionalWrapper>
      )}
      {isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[{ marketCountry: [MarketCountryCode.Denmark] }]}
        >
          <ApplicationDataTextField
            applicationID={applicationID}
            name="CoApplicantCompanyOrganizationNumber"
            label="Company CVR"
          />
        </ApplicationConditionalWrapper>
      )}
      {!isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              marketCountry: [
                MarketCountryCode.Norway,
                MarketCountryCode.Sweden,
                MarketCountryCode.Finland,
              ],
            },
          ]}
        >
          <ApplicationDataTextField
            applicationID={applicationID}
            name="Employer"
            label="Employer"
          />
        </ApplicationConditionalWrapper>
      )}
      {isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              productType: [Product.UnsecuredLoan, Product.SecuredLoan],
              marketCountry: [
                MarketCountryCode.Norway,
                MarketCountryCode.Sweden,
                MarketCountryCode.Finland,
              ],
            },
          ]}
        >
          <ApplicationDataTextField
            applicationID={applicationID}
            name="CoApplicantEmployer"
            label="Employer"
          />
        </ApplicationConditionalWrapper>
      )}
      {!isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              marketCountry: [MarketCountryCode.Finland],
              productType: [Product.UnsecuredLoan],
              broker: [Broker.Zmarta],
            },
          ]}
        >
          <ApplicationDataSelectField
            applicationID={applicationID}
            name="EmployerIndustry"
            label="Employer's industry"
            options={[
              { value: null, label: '-' },
              ...Object.values(EmployerIndustry).map((value) => ({
                value,
              })),
            ]}
          />
        </ApplicationConditionalWrapper>
      )}
      {!isCoApplicant && (
        <ApplicationDataDateField
          applicationID={applicationID}
          name="EmploymentStatusSince"
          label="Start"
        />
      )}
      {isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              productType: [Product.UnsecuredLoan, Product.SecuredLoan],
            },
          ]}
        >
          <ApplicationDataDateField
            applicationID={applicationID}
            name="CoApplicantEmploymentStatusSince"
            label="Start"
          />
        </ApplicationConditionalWrapper>
      )}
      {!isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              productType: [Product.UnsecuredLoan],
              marketCountry: [
                MarketCountryCode.Finland,
                MarketCountryCode.Sweden,
              ],
            },
          ]}
        >
          <ApplicationDataDateField
            applicationID={applicationID}
            name="EmploymentStatusUntil"
            label="End"
          />
        </ApplicationConditionalWrapper>
      )}
      {isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              productType: [Product.UnsecuredLoan],
              marketCountry: [MarketCountryCode.Sweden],
            },
          ]}
        >
          <ApplicationDataDateField
            applicationID={applicationID}
            name="CoApplicantEmploymentStatusUntil"
            label="End"
          />
        </ApplicationConditionalWrapper>
      )}
      {!isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              productType: [Product.UnsecuredLoan],
              marketCountry: [MarketCountryCode.Finland],
            },
          ]}
        >
          <ApplicationDataTextField
            applicationID={applicationID}
            name="JobTitle"
            label="Ocupation"
          />
        </ApplicationConditionalWrapper>
      )}
      {!isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              productType: [Product.UnsecuredLoan],
              marketCountry: [MarketCountryCode.Finland],
              broker: [Broker.Zmarta],
            },
          ]}
        >
          <ApplicationDataSelectField
            applicationID={applicationID}
            name="JobPosition"
            label="Position"
            options={[
              { value: null, label: '-' },
              ...Object.values(JobPosition).map((value) => ({
                value,
              })),
            ]}
          />
        </ApplicationConditionalWrapper>
      )}
      {!isCoApplicant && (
        <ApplicationDataCurrencyField
          applicationID={applicationID}
          name="GrossIncomeMonthly"
          label="Monthly income before tax"
          onChangeSideEffect={(value) =>
            setValue('GrossIncomeYearly', value * 12)
          }
        />
      )}
      {!isCoApplicant && (
        <ApplicationDataCurrencyField
          applicationID={applicationID}
          name="GrossIncomeYearly"
          label="Yearly income before tax"
          onChangeSideEffect={(value) =>
            setValue('GrossIncomeMonthly', Math.round(value / 12))
          }
        />
      )}
      {isCoApplicant && (
        <ApplicationDataCurrencyField
          applicationID={applicationID}
          name="CoApplicantGrossIncomeYearly"
          label="Yearly income before tax"
        />
      )}
      {!isCoApplicant && (
        <ApplicationDataCurrencyField
          applicationID={applicationID}
          name="NetIncomeMonthly"
          label="Monthly income after tax"
        />
      )}
      {isCoApplicant && (
        <ApplicationDataCurrencyField
          applicationID={applicationID}
          name="CoApplicantNetIncomeYearly"
          label="Yearly income after tax"
        />
      )}
      {!isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              marketCountry: [
                MarketCountryCode.Finland,
                MarketCountryCode.Denmark,
              ],
              productType: [Product.UnsecuredLoan],
            },
          ]}
        >
          <ApplicationDataCurrencyField
            applicationID={applicationID}
            name="ExtraIncomeMonthly"
            label="Other monthly income"
          />
        </ApplicationConditionalWrapper>
      )}
      {!isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              marketCountry: [MarketCountryCode.Norway],
              productType: [Product.SecuredLoan],
            },
          ]}
        >
          <ApplicationDataCurrencyField
            applicationID={applicationID}
            name="ExtraIncomeMonthly"
            label="Other monthly income"
            isEditable={false}
          />
        </ApplicationConditionalWrapper>
      )}
      {!isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              marketCountry: [MarketCountryCode.Norway],
              productType: [Product.UnsecuredLoan, Product.CreditCard],
            },
          ]}
        >
          <ApplicationDataCurrencyField
            applicationID={applicationID}
            name="ExtraIncomeRental"
            label="Other income rental"
          />
        </ApplicationConditionalWrapper>
      )}
      {!isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              marketCountry: [MarketCountryCode.Norway],
              productType: [Product.SecuredLoan],
            },
          ]}
        >
          <ApplicationDataCurrencyField
            applicationID={applicationID}
            name="ExtraIncomeRental"
            label="Other income rental"
            onChangeSideEffect={(value) => {
              setValue(
                'ExtraIncomeMonthly',
                (getValues('ExtraIncomeChildSupport') ?? 0) + value,
              );
            }}
          />
        </ApplicationConditionalWrapper>
      )}
      {!isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              marketCountry: [MarketCountryCode.Norway],
              productType: [Product.UnsecuredLoan, Product.CreditCard],
            },
          ]}
        >
          <ApplicationDataCurrencyField
            applicationID={applicationID}
            name="ExtraIncomeChildSupport"
            label="Other income child support"
          />
        </ApplicationConditionalWrapper>
      )}
      {!isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              marketCountry: [MarketCountryCode.Norway],
              productType: [Product.SecuredLoan],
            },
          ]}
        >
          <ApplicationDataCurrencyField
            applicationID={applicationID}
            name="ExtraIncomeChildSupport"
            label="Other income child support"
            onChangeSideEffect={(value) => {
              setValue(
                'ExtraIncomeMonthly',
                (getValues('ExtraIncomeRental') ?? 0) + value,
              );
            }}
          />
        </ApplicationConditionalWrapper>
      )}
      {!isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              marketCountry: [
                MarketCountryCode.Finland,
                MarketCountryCode.Denmark,
              ],
              productType: [Product.UnsecuredLoan],
            },
          ]}
        >
          <ApplicationDataCurrencyField
            applicationID={applicationID}
            name="LivingCostMonthly"
            label="Living cost monthly"
          />
        </ApplicationConditionalWrapper>
      )}
      {isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              marketCountry: [MarketCountryCode.Finland],
              productType: [Product.UnsecuredLoan],
            },
          ]}
        >
          <ApplicationDataCurrencyField
            applicationID={applicationID}
            name="CoApplicantLivingCostMonthly"
            label="Living cost monthly"
          />
        </ApplicationConditionalWrapper>
      )}
      {!isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              marketCountry: [MarketCountryCode.Denmark],
            },
          ]}
        >
          <ApplicationDataNumberField
            applicationID={applicationID}
            name="YearsAsBankCustomer"
            label="Years with primary bank"
          />
        </ApplicationConditionalWrapper>
      )}
      {!isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              marketCountry: [MarketCountryCode.Denmark],
            },
          ]}
        >
          <ApplicationDataSelectField
            applicationID={applicationID}
            name="HasDankort"
            label="Dankort or Visa Dankort"
            options={[
              { value: true, label: 'Yes' },
              { value: false, label: 'No' },
            ]}
          />
        </ApplicationConditionalWrapper>
      )}
      {!isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              marketCountry: [MarketCountryCode.Denmark],
            },
          ]}
        >
          <ApplicationDataSelectField
            applicationID={applicationID}
            name="HasOtherPaymentCards"
            label="Mastercard or other payment cards"
            options={[
              { value: true, label: 'Yes' },
              { value: false, label: 'No' },
            ]}
          />
        </ApplicationConditionalWrapper>
      )}
      {!isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              marketCountry: [MarketCountryCode.Denmark],
            },
          ]}
        >
          <ApplicationDataSelectField
            applicationID={applicationID}
            name="HasUnemploymentFund"
            label="Member of unemployment fund"
            options={[
              { value: true, label: 'Yes' },
              { value: false, label: 'No' },
            ]}
          />
        </ApplicationConditionalWrapper>
      )}
      {isCoApplicant && (
        <ApplicationConditionalWrapper
          whitelist={[
            {
              marketCountry: [MarketCountryCode.Denmark],
            },
          ]}
        >
          <ApplicationDataSelectField
            applicationID={applicationID}
            name="CoApplicantHasUnemploymentFund"
            label="Member of unemployment fund"
            options={[
              { value: true, label: 'Yes' },
              { value: false, label: 'No' },
            ]}
          />
        </ApplicationConditionalWrapper>
      )}
    </ApplicantDataApplicantTabPanelSection>
  );
};
